import React from 'react'
import styled from 'styled-components'
import { navigate } from 'gatsby'
import {
  Container,
  Box,
  Button,
  P,
  H1,
  IconContainer,
  ScreenReaderText,
  SkeletonLoading,
  SmartSearch,
} from '@allied-solutions/affinity'
import {
  Plus,
  Clock,
  InfoSolid,
} from '@allied-solutions/affinity-react-icons/dist/standard'
import { Success } from '@allied-solutions/affinity-react-icons/dist/duotone'
import { useWindowWidth, useStore } from '../../hooks/'
import { theme } from '../../theme'

const StyledContainer = styled(Container)`
  border-radius: ${props => props.theme.radii[6]};
  box-shadow: ${props => props.theme.shadows.md};
  background-color: ${props => props.theme.colors.white};
  padding: ${props => props.theme.space['4xl']};
`

export const DashboardHeader = ({
  isBorrowerError,
  isCoverageRequirementsError,
  isLoading,
  hasVisibleAlerts,
  itemsCount,
  recentSearches,
  searchInputValue,
  isSearchLoading,
  onSearchSubmit,
  onSearchInputValueChange,
  onSearchStateChange,
  onSearchItemSelect,
  onRemoveRecentSearch,
}) => {
  const windowWidth = useWindowWidth()
  const isMobile = windowWidth < Number.parseInt(theme.breakpoints[1])
  const { state } = useStore()
  const firstName = state?.userData?.Name?.['Given']
  const hasFirstName =
    typeof firstName === 'string' && firstName.trim().length > 0

  let addReferenceIdButton = null
  if (!isMobile && !isBorrowerError) {
    if (isLoading) {
      addReferenceIdButton = (
        <SkeletonLoading width="160px" height="40px" loading />
      )
    } else {
      addReferenceIdButton = (
        <Button
          icon={Plus}
          size={itemsCount >= 5 ? 'md' : 'lg'}
          onClick={() => {
            navigate('/enter-reference-id/')
          }}
        >
          <ScreenReaderText as="span">Add </ScreenReaderText>
          Reference ID
        </Button>
      )
    }
  }

  const headerPaddingTop = hasVisibleAlerts ? '4xl' : ['6xl', '4xl']

  const headerStandard = () => {
    return (
      <Container
        id="contentContainerId"
        px="0"
        maxWidth="800px"
        mb="8xl"
        pt={headerPaddingTop}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box id="headerId" display="flex" alignItems="center">
            <IconContainer icon={Success} mr="4" />
            <Box>
              <P typeStyle="titleLarge.bold" display="block" mb={2}>
                {isBorrowerError || isCoverageRequirementsError || !hasFirstName
                  ? 'Hello!'
                  : `Hi, ${firstName.trim()}!`}
              </P>
              <H1
                mb="0"
                typeStyle="bodyMedium"
                color="text.secondary"
                pr={['xl', 0]}
              >
                Check the status and insurance verification for your loan(s).
              </H1>
            </Box>
          </Box>
          {addReferenceIdButton}
        </Box>
      </Container>
    )
  }

  const headerWithSearch = () => {
    return (
      <StyledContainer
        id="contentContainerId"
        px="0"
        maxWidth="800px"
        mb="8xl"
        mt="4xl"
        pt={headerPaddingTop}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Box id="headerId" display="flex" alignItems="center">
            <IconContainer icon={Success} mr="4" />
            <Box>
              <P typeStyle="titleLarge.bold" display="block" mb={2}>
                {isBorrowerError || isCoverageRequirementsError || !hasFirstName
                  ? 'Hello!'
                  : `Hi, ${firstName.trim()}!`}
              </P>
              <H1
                mb="0"
                typeStyle="bodyMedium"
                color="text.secondary"
                pr={['xl', 0]}
              >
                Check the status and insurance verification for your loan(s).
              </H1>
            </Box>
          </Box>
          {addReferenceIdButton}
        </Box>
        <Box mt="4xl">
          <SmartSearch
            items={recentSearches}
            id="dashboard-smartSearch"
            label="Search for a loan associated with this profile"
            search
            submitButton="Search"
            inputValue={searchInputValue}
            onSubmit={onSearchSubmit}
            onInputValueChange={onSearchInputValueChange}
            onStateChange={onSearchStateChange}
            onSelectedItemChange={onSearchItemSelect}
            isLoading={isSearchLoading}
            menuOptions={{
              title: 'Recent Searches',
              itemIcon: Clock,
              onRemoveItemHandler: onRemoveRecentSearch,
              info: (
                <span>
                  <strong>Hint:</strong> You can search by the following terms:
                  Reference ID, Loan Number, Borrower Name, and Collateral
                  Details (ex. Address, YMM, etc.).
                </span>
              ),
              infoIcon: InfoSolid,
            }}
          />
        </Box>
      </StyledContainer>
    )
  }

  return itemsCount >= 5 ? headerWithSearch() : headerStandard()
}

export default DashboardHeader
